import { DRUID_PATH, NIGHTMARES } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { AutumnRules, AutumnState, BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import type { LeafLayout, PastLayout, UnderLayout } from './druid-autumn-layout.ts';

/**
 * The autumn wood over Los Hornos (docs/sendas/druida.md, chapter 3; the grounds in battle/druid-autumn-layout.ts).
 *
 * - Under the leaves (act 1, `leaves`): Ceda walks her way up to Olmo's maple while the hero keeps by her and nothing
 *   that came up is at her. Stalkers go for her under the leaves, out of reach (`autumnHidden`, read by combat's
 *   `isRevealed`): only their furrow shows; by her they heave the leaves (3 s) and come up. The druid's Raíz (standing
 *   still) pulls up every one under the leaves close by, and holds it a moment. Weavers keep off her and spin silk at
 *   her (3 s, a line on the ground; struck enough meanwhile, they let go): a cocoon stops her and squeezes her.
 * - The memory, Olmo's autumn (act 2, `blight`): the rot comes down three roots from the maple to the houses, forking;
 *   its heads go a hex at a time and nothing strikes them. Olmo cuts a root standing on it (his Raíz): a head that
 *   comes to it dies there; one already past goes on. A head at the houses loses it. Paz and the colliers chop at what
 *   comes near; rootwights out of the forks, leeches out of the rot.
 * - What lies below (act 3, the test, `under`): the net's cells; a tap on one, and the hero standing still on it cuts
 *   everything under it. The rot wakes with the first cut and goes a hex at a time along its four roots; a cut ahead
 *   stops one; the rot in the maple eats its branches; a cut in vain or on a sound root costs one. The broodmother
 *   leaps at the hero, webs the maple and lets out her brood.
 *
 * Cost: a handful of points a tick (the stalkers and weavers against Ceda and the hero, the heads, the fronts); the
 * people's blows once a second, each against the nightmares once (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

type St = AutumnState;
type Pt = { x: number; y: number };
type BNode = NonNullable<AutumnRules['blight']>['nodes'][number];

const fresh = (): St => ({
  done: -1,
  lost: 0,
  t: 0,
  job: -1,
  held: 0,
  cedaAt: 1,
  walk: 0,
  sk: {},
  wv: {},
  stalkN: 0,
  weaveN: 0,
  cocoon: 0,
  squeeze: 0,
  caught: 0,
  surf: 0,
  spins: 0,
  rot: [],
  heads: [],
  wave: 0,
  cuts: 0,
  forks: 0,
  leech: 0,
  chop: 0,
  low: 0,
  near: 0,
  front: [],
  block: [],
  cut: [],
  woke: -1,
  next: 0,
  lostB: 0,
  inMaple: [],
  webs: {},
  webNext: 0,
  broodNext: 0,
  boss: 0,
  bt: -1,
  bx: 0,
  by: 0,
  bossNext: 0,
  wrong: 0,
  vain: 0,
  blocks: 0,
  leaps: 0,
  silk: 0,
  pend: [],
});

/* ---------- rules from the layouts (the fight's builder in campaign/sendas.ts and the drawing build them the same) ---------- */

export const leavesRules = (L: LeafLayout): AutumnRules => {
  const P = DRUID_PATH.leaves;
  const m = hexToWorld(L.maple.q, L.maple.r);
  return {
    kind: 'leaves',
    bossHp: 0,
    leaves: {
      way: L.way.map((p) => ({ ...p })),
      maple: { x: m.x, y: m.y },
      rim: L.rim.map((p) => ({ ...p })),
      pace: P.pace,
      lead: P.lead,
      fear: P.fear,
      reach: P.reach,
      still: P.still,
      stalk: { ...P.stalk },
      weave: { ...P.weave },
    },
  };
};

export const blightRules = (L: PastLayout): AutumnRules => {
  const B = DRUID_PATH.blight;
  return {
    kind: 'blight',
    bossHp: 0,
    blight: {
      nodes: L.nodes.map((n) => ({ ...hexToWorld(n.q, n.r), up: n.up, down: [...n.down], src: n.src })),
      srcs: [...L.srcs],
      ends: [...L.ends],
      waves: B.waves.map((w) => ({ ...w })),
      per: B.per,
      cut: B.cut,
      reach: B.reach,
      lash: { ...B.lash },
      wightBp: B.wightBp,
      chopR: B.chopR,
      pazBp: B.paz.chopBp,
      collierBp: B.colliers.chopBp,
      leech: { ...B.leech },
      rim: L.rim.map((p) => ({ ...p })),
    },
  };
};

export const underRules = (L: UnderLayout, bossHp: number, U: typeof DRUID_PATH.under = DRUID_PATH.under): AutumnRules => {
  const m = hexToWorld(L.maple.q, L.maple.r);
  const k = hexToWorld(L.knot.q, L.knot.r);
  return {
    kind: 'under',
    bossHp,
    under: {
      cells: L.cells.map((h) => hexToWorld(h.q, h.r)),
      on: L.on.map((x) => [...x]),
      strands: L.strands.map((st, i) => ({ at: [...(L.at[i] as number[])], rot: st.rot })),
      vis: [...L.vis],
      mush: [...L.mush],
      black: [...L.black],
      maple: { x: m.x, y: m.y },
      knot: { x: k.x, y: k.y },
      rim: L.rim.map((p) => ({ ...p })),
      pre: U.pre,
      bite: U.bite,
      step: U.step,
      wake: U.wake,
      cut: U.cut,
      reach: U.reach,
      branches: U.branches,
      eat: U.eat,
      boss: { ...U.boss },
      web: { ...U.web },
      brood: { ...U.brood },
      puffBp: U.puffBp,
    },
  };
};

/* ---------- helpers ---------- */

const heroOf = (s: BattleState): Hero | undefined => s.heroes.find((x) => x.downTicks === 0);
const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** A nightmare of the act at a point, its life scaled as the fight's are (`bp`: a share of that). */
const spawn = (s: BattleState, kind: 'stalker' | 'weaver' | 'cocoon' | 'rootwight' | 'leech' | 'puffball' | 'spiderling', x: number, y: number, bp = 10000): Enemy => {
  const e = spawnEnemy(s, kind, 1, false, 0, x, y);
  let hp = e.maxHp;
  if (s.rules?.enemyHpBp) hp = mulBp(hp, s.rules.enemyHpBp);
  e.hp = e.maxHp = imax(1, mulBp(hp, bp));
  return e;
};

/** A blow on a person that isn't a nightmare's standing at them (a cocoon squeezing, a lash): its blows off their life. */
const hurtNpc = (s: BattleState, n: SendaNpc | undefined, blows: number): void => {
  if (!n || n.hp <= 0 || blows <= 0) return;
  n.hp -= blows;
  n.hit = true;
  ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0) {
    n.hp = 0;
    ev(s, { t: 'senda', kind: 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/** Whether the hero has stood still long enough for its Raíz. */
const rooted = (h: Hero, still: number): boolean => !h.moving && (h.still ?? 0) >= still;

/* ---------- act 1: under the leaves ---------- */

const leavesTick = (s: BattleState, r: NonNullable<AutumnRules['leaves']>, w: St, cue: (c: string) => void): void => {
  const ceda = npcOf(s, 'ceda');
  const h = heroOf(s);
  if (!ceda || w.done >= 0) return;
  // The stalkers: one each `every` from `first`, `max` at once.
  const alive = Object.keys(w.sk).filter((id) => (findEnemy(s, Number(id))?.hp ?? 0) > 0).length;
  if (s.waveT >= r.stalk.first + w.stalkN * r.stalk.every && alive < r.stalk.max) {
    const p = r.rim[(w.stalkN * 3 + rngInt(s.rng, 2)) % r.rim.length] as Pt;
    const e = spawn(s, 'stalker', p.x, imin(p.y, ceda.y + 2600));
    w.sk[e.id] = [0, 0];
    w.stalkN++;
    ev(s, { t: 'senda', kind: 'furrow', npc: '', x: e.x, y: e.y });
    cue(`furrow${w.stalkN}`);
  }
  // The weavers.
  const wvAlive = Object.keys(w.wv).filter((id) => (findEnemy(s, Number(id))?.hp ?? 0) > 0).length;
  if (s.waveT >= r.weave.first + w.weaveN * r.weave.every && wvAlive < r.weave.max) {
    const p = r.rim[(w.weaveN * 5 + 2) % r.rim.length] as Pt;
    const e = spawn(s, 'weaver', p.x, imin(p.y, ceda.y + 1000), r.weave.hpBp);
    w.wv[e.id] = [s.tick + r.weave.spin, -1, 0, 0, 0];
    w.weaveN++;
  }
  // The druid's Raíz: every one under the leaves close by pulled up and held.
  if (h && rooted(h, r.still)) {
    for (const id of Object.keys(w.sk)) {
      const m = w.sk[Number(id)] as number[];
      const e = findEnemy(s, Number(id));
      if (!e || e.hp <= 0 || (m[0] !== 0 && m[0] !== 1 && m[0] !== 3) || !withinRange(e.x, e.y, h.x, h.y, r.stalk.rootR)) continue;
      m[0] = 4;
      m[1] = s.tick + r.stalk.hold;
      e.stun = imax(e.stun, r.stalk.hold);
      w.caught++;
      ev(s, { t: 'senda', kind: 'caught', npc: '', x: e.x, y: e.y });
      cue(`caught${w.caught}`);
    }
  }
  // A cocoon on her: it squeezes while it holds.
  if (w.cocoon) {
    const c = findEnemy(s, w.cocoon);
    if (!c || c.hp <= 0) {
      w.cocoon = 0;
      ev(s, { t: 'senda', kind: 'free', npc: 'ceda', x: ceda.x, y: ceda.y });
    } else if (s.tick >= w.squeeze) {
      w.squeeze = s.tick + r.weave.squeeze;
      hurtNpc(s, ceda, 1);
    }
  }
  // Ceda's way: on while the hero is by her, nothing up is at her and nothing holds her.
  const goal = r.way[w.cedaAt] as Pt | undefined;
  let walk = !!goal && !w.cocoon && !!h && withinRange(h.x, h.y, ceda.x, ceda.y, r.lead);
  if (walk) {
    for (const e of s.enemies) {
      if (e.hp <= 0 || e.kind === 'cocoon' || autumnHidden(s, e)) continue;
      if (withinRange(e.x, e.y, ceda.x, ceda.y, r.fear)) {
        walk = false;
        break;
      }
    }
  }
  w.walk = walk ? 1 : 0;
  ceda.moving = walk;
  if (walk && goal) {
    stepToward(ceda, goal.x, goal.y, r.pace);
    if (withinRange(ceda.x, ceda.y, goal.x, goal.y, 60)) w.cedaAt++;
  }
  if (w.cedaAt >= r.way.length) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'glade', npc: 'ceda', x: ceda.x, y: ceda.y });
    cue('maple');
  }
};

/** A stalker: under the leaves to Ceda (out of reach), heaving by her, up a while, under again; held by the Raíz. */
const stalkerHold = (s: BattleState, r: NonNullable<AutumnRules['leaves']>, w: St, e: Enemy, cue: (c: string) => void): boolean => {
  const m = w.sk[e.id];
  const ceda = npcOf(s, 'ceda');
  if (!m || !ceda) return false;
  const mode = m[0] as number;
  if (mode === 4) {
    // Held by the roots: it can't go under nor away.
    e.anim = 'idle';
    if (s.tick >= (m[1] as number)) {
      m[0] = 2;
      m[1] = s.tick + r.stalk.up;
    }
    return true;
  }
  if (mode === 2) {
    if (s.tick < (m[1] as number)) return false;
    // Under again, off into the leaves a while.
    m[0] = 3;
    m[1] = s.tick + r.stalk.up;
    ev(s, { t: 'senda', kind: 'dive', npc: '', x: e.x, y: e.y });
    return true;
  }
  if (mode === 1) {
    e.anim = 'idle';
    if (s.tick < (m[1] as number)) return true;
    m[0] = 2;
    m[1] = s.tick + r.stalk.up;
    w.surf++;
    ev(s, { t: 'senda', kind: 'surface', npc: '', x: e.x, y: e.y });
    cue(`surf${w.surf}`);
    return true;
  }
  e.anim = 'walk';
  if (mode === 3) {
    // Away from her through the leaves, then back.
    const dx = e.x - ceda.x;
    const dy = e.y - ceda.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    stepToward(e, ceda.x + idiv(dx * 4200, d), ceda.y + idiv(dy * 4200, d), r.stalk.pace);
    if (s.tick >= (m[1] as number)) m[0] = 0;
    return true;
  }
  stepToward(e, ceda.x, ceda.y, r.stalk.pace);
  if (withinRange(e.x, e.y, ceda.x, ceda.y, r.stalk.surfR)) {
    m[0] = 1;
    m[1] = s.tick + r.stalk.warn;
    ev(s, { t: 'senda', kind: 'heave', npc: '', x: e.x, y: e.y, n: r.stalk.warn });
  }
  return true;
};

/** A weaver: off Ceda at its range, silk at her (warned along a line; struck enough meanwhile, it lets go). */
const weaverHold = (s: BattleState, r: NonNullable<AutumnRules['leaves']>, w: St, e: Enemy, px: number, cue: (c: string) => void): boolean => {
  const m = w.wv[e.id];
  const ceda = npcOf(s, 'ceda');
  const h = heroOf(s);
  if (!m || !ceda) return false;
  e.stun = 0;
  // Spinning: warned, the line held to her; struck a fifth of its life meanwhile, it lets go.
  if ((m[1] as number) >= 0) {
    e.anim = 'attack';
    if ((m[2] as number) - e.hp >= idiv(e.maxHp, 5)) {
      m[1] = -1;
      m[0] = s.tick + r.weave.spin;
      ev(s, { t: 'senda', kind: 'scare', npc: '', x: e.x, y: e.y });
      return true;
    }
    if (s.tick < (m[1] as number)) return true;
    m[1] = -1;
    m[0] = s.tick + r.weave.spin;
    if (!w.cocoon) {
      const c = spawn(s, 'cocoon', ceda.x, ceda.y + 40, r.weave.cocoonHp);
      w.cocoon = c.id;
      w.squeeze = s.tick + r.weave.squeeze;
      ev(s, { t: 'senda', kind: 'cocoon', npc: 'ceda', x: ceda.x, y: ceda.y });
      cue(`cocoon${++w.spins}`);
    }
    return true;
  }
  // A bite at the hero close by.
  if (h && withinRange(e.x, e.y, h.x, h.y, NIGHTMARES.weaver.reach + 200)) {
    if (e.cd === 0) {
      e.cd = e.reload;
      e.anim = 'attack';
      e.animT = 0;
      damageHero(s, h, e.damage, e, 'physical');
    }
    return true;
  }
  // Off her at its range, then the silk.
  const dx = e.x - ceda.x;
  const dy = e.y - ceda.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const tx = ceda.x + idiv(dx * r.weave.range, d);
  const ty = ceda.y + idiv(dy * r.weave.range, d);
  if (!withinRange(e.x, e.y, tx, ty, 400)) {
    e.anim = 'walk';
    stepToward(e, tx, ty, px);
  } else e.anim = 'idle';
  if (s.tick >= (m[0] as number) && withinRange(e.x, e.y, ceda.x, ceda.y, r.weave.range + 900) && !w.cocoon) {
    m[1] = s.tick + r.weave.warn;
    m[2] = e.hp;
    m[3] = ceda.x;
    m[4] = ceda.y;
    ev(s, { t: 'senda', kind: 'spin', npc: `${ceda.x},${ceda.y}`, x: e.x, y: e.y, n: r.weave.warn });
    cue(`spin${w.spins + 1}`);
  }
  return true;
};

/* ---------- act 2: the memory, Olmo's autumn ---------- */

/** The hero sent to a root (the memory) or a cell (the test): to stand on it. */
export const autumnOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.autumn;
  const w = s.senda?.autumn;
  if (!r || !w || w.done >= 0 || at < 0) return;
  const p = r.blight ? r.blight.nodes[at] : r.under ? r.under.cells[at] : undefined;
  if (!p) return;
  if (r.blight && w.rot[at] === 2) return;
  if (r.under && w.cut[at]) return;
  w.job = at;
  w.held = 0;
  h.target = 0;
  h.forced = false;
  orderMove(s, h, p.x, p.y);
};

const blightTick = (s: BattleState, r: NonNullable<AutumnRules['blight']>, w: St, cue: (c: string) => void): void => {
  const h = heroOf(s);
  if (w.done >= 0) return;
  if (!w.rot.length) w.rot = r.nodes.map(() => 0);
  // Olmo on a root, still: his Raíz cuts it.
  if (h && w.job >= 0) {
    const p = r.nodes[w.job] as Pt;
    if (w.rot[w.job] === 2) w.job = -1;
    else if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
      w.held = 0;
      if (!h.moving) w.job = -1;
    } else if (++w.held >= r.cut) {
      w.rot[w.job] = 2;
      w.cuts++;
      ev(s, { t: 'senda', kind: 'rootCut', npc: '', x: p.x, y: p.y, n: w.job });
      cue(`cut${w.cuts}`);
      w.job = -1;
      w.held = 0;
    }
  }
  // The waves: a head at a root's top.
  while (w.wave < r.waves.length && s.waveT >= (r.waves[w.wave] as { t: number }).t) {
    const src = r.srcs[(r.waves[w.wave] as { src: number }).src] as number;
    w.wave++;
    // (A cut stops one head and is spent: the root closes black behind it.)
    if (w.rot[src] === 2) {
      w.rot[src] = 3;
      ev(s, { t: 'senda', kind: 'headDie', npc: '', x: (r.nodes[src] as Pt).x, y: (r.nodes[src] as Pt).y, n: src });
      cue('stopped');
      continue;
    }
    w.rot[src] = 1;
    w.heads.push({ i: src, at: s.tick + r.per });
    const p = r.nodes[src] as Pt;
    ev(s, { t: 'senda', kind: 'rotIn', npc: '', x: p.x, y: p.y, n: src });
    cue(`wave${w.wave}`);
  }
  // The heads, a hex at a time, splitting where the root forks; a cut stops one; one at the houses loses it.
  const next: { i: number; at: number }[] = [];
  for (const hd of w.heads) {
    if (s.tick < hd.at) {
      next.push(hd);
      continue;
    }
    const n = r.nodes[hd.i] as BNode;
    if (r.ends.includes(hd.i)) {
      if (w.lost === 0) w.lost = 1;
      ev(s, { t: 'senda', kind: 'houses', npc: '', x: n.x, y: n.y, n: hd.i });
      continue;
    }
    if (n.down.length > 1) {
      w.forks++;
      cue(`fork${w.forks}`);
      if (w.forks % 2 === 1) spawn(s, 'rootwight', n.x, n.y, r.wightBp);
    }
    for (const c of n.down) {
      const p = r.nodes[c] as Pt;
      if (w.rot[c] === 2) {
        w.rot[c] = 3;
        ev(s, { t: 'senda', kind: 'headDie', npc: '', x: p.x, y: p.y, n: c });
        cue('stopped');
        continue;
      }
      w.rot[c] = 1;
      next.push({ i: c, at: s.tick + r.per });
      ev(s, { t: 'senda', kind: 'rotStep', npc: '', x: p.x, y: p.y, n: c });
      for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, p.x, p.y, r.lash.r)) damageHero(s, x, mulBp(x.stats.maxHp, r.lash.bp), null, 'physical', true);
      const y = p.y;
      if (w.near === 0 && y > 3 * 866) {
        w.near = 1;
        cue('near');
      }
    }
  }
  w.heads = next;
  // Leeches out of the rot, by a head.
  if (s.waveT >= r.leech.first && s.waveT >= w.leech && w.heads.length) {
    w.leech = s.waveT + r.leech.every;
    const hd = w.heads[rngInt(s.rng, w.heads.length)] as { i: number };
    const p = r.nodes[hd.i] as Pt;
    for (let k = 0; k < r.leech.n; k++) spawn(s, 'leech', p.x + rngRange(s.rng, -500, 500), p.y + rngRange(s.rng, -500, 500));
  }
  // Paz and the colliers: hacha y raíz, the nearest nightmare by each, once a second.
  if (s.tick >= w.chop) {
    w.chop = s.tick + 12;
    for (const n of s.senda?.npcs ?? []) {
      if (n.hp <= 0) continue;
      let best: Enemy | null = null;
      let bd = r.chopR * r.chopR;
      for (const e of s.enemies) {
        if (e.hp <= 0) continue;
        const d = dist2(e.x, e.y, n.x, n.y);
        if (d < bd) {
          bd = d;
          best = e;
        }
      }
      if (!best) continue;
      // (Off its life straight: the step's reap takes it when it falls, as any other.)
      best.hp = imax(0, best.hp - imax(1, mulBp(best.maxHp, n.id === 'paz' ? r.pazBp : r.collierBp)));
      ev(s, { t: 'senda', kind: 'chop', npc: n.id, x: best.x, y: best.y });
    }
  }
  // Olmo low for the first time: Paz's line.
  if (h && w.low === 0 && h.hp * 2 < h.stats.maxHp) {
    w.low = 1;
    cue('olmoHalf');
  }
  if (w.lost === 0 && w.wave >= r.waves.length && !w.heads.length) {
    w.done = s.tick;
    const m = r.nodes[r.srcs[1] as number] as Pt;
    ev(s, { t: 'senda', kind: 'seed', npc: '', x: m.x, y: m.y - 1700 });
    cue('seed');
  }
};

/* ---------- act 3: what lies below ---------- */

const underTick = (s: BattleState, r: NonNullable<AutumnRules['under']>, w: St, cue: (c: string) => void): void => {
  const h = heroOf(s);
  if (w.done >= 0) return;
  if (!w.cut.length) {
    w.cut = r.cells.map(() => 0);
    w.front = r.strands.map((st) => (st.rot ? r.pre : 0));
    w.block = r.strands.map(() => -1);
    w.inMaple = r.strands.map(() => -1);
  }
  const branch = (why: string, x: number, y: number): void => {
    w.lostB++;
    ev(s, { t: 'senda', kind: 'branch', npc: why, x, y, n: w.lostB });
    cue(`branch${w.lostB}`);
    if (w.lostB >= r.branches && w.lost === 0) w.lost = 1;
  };
  // The hero on a cell, still: its Raíz cuts everything under it.
  if (h && w.job >= 0) {
    const p = r.cells[w.job] as Pt;
    if (w.cut[w.job]) w.job = -1;
    else if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
      w.held = 0;
      if (!h.moving) w.job = -1;
    } else if (++w.held >= r.cut) {
      const i = w.job;
      w.cut[i] = 1;
      w.job = -1;
      w.held = 0;
      let good = 0;
      let bad = 0;
      for (const sIdx of r.on[i] as number[]) {
        const st = r.strands[sIdx] as { at: number[]; rot: boolean };
        if (!st.rot) {
          bad++;
          continue;
        }
        const k = st.at.indexOf(i);
        if (k <= (w.front[sIdx] as number) || (w.block[sIdx] as number) >= 0) {
          if ((w.block[sIdx] as number) < 0) bad++;
          continue;
        }
        w.block[sIdx] = k;
        good++;
      }
      ev(s, { t: 'senda', kind: 'rootCut', npc: good && !bad ? 'good' : bad ? 'bad' : 'vain', x: p.x, y: p.y, n: i });
      // The rot bites back up the druid's own root.
      if ((r.on[i] as number[]).some((x) => (r.strands[x] as { rot: boolean }).rot)) damageHero(s, h, mulBp(h.stats.maxHp, r.bite), null, 'poison', true);
      if (good) {
        w.blocks += good;
        cue(`block${w.blocks}`);
      }
      if (bad || !good) {
        const sound = (r.on[i] as number[]).some((x) => !(r.strands[x] as { rot: boolean }).rot);
        if (sound) cue(`wrong${++w.wrong}`);
        else cue(`vain${++w.vain}`);
        branch(sound ? 'sound' : 'vain', p.x, p.y);
      }
      if (w.woke < 0) {
        w.woke = s.tick;
        w.next = s.tick + r.step;
        cue('wake');
      }
    }
  }
  if (w.woke < 0 && s.waveT >= r.wake) {
    w.woke = s.tick;
    w.next = s.tick + r.step;
    cue('wake');
  }
  // The rot, a hex at a time along each rotten root it still has.
  if (w.woke >= 0 && s.tick >= w.next) {
    w.next = s.tick + r.step;
    let moved = -1;
    for (let k = 0; k < r.strands.length; k++) {
      const st = r.strands[k] as { at: number[]; rot: boolean };
      if (!st.rot || (w.inMaple[k] as number) >= 0) continue;
      const f = w.front[k] as number;
      const b = w.block[k] as number;
      if (b >= 0 && f + 1 >= b) continue;
      if (f + 1 >= st.at.length) {
        w.inMaple[k] = s.tick + r.eat;
        ev(s, { t: 'senda', kind: 'inMaple', npc: '', x: r.maple.x, y: r.maple.y, n: k });
        cue(`in${w.inMaple.filter((x) => x >= 0).length}`);
        branch('rot', r.maple.x, r.maple.y);
        continue;
      }
      w.front[k] = f + 1;
      moved = k;
      const c = r.cells[st.at[f + 1] as number] as Pt;
      ev(s, { t: 'senda', kind: 'rotStep', npc: '', x: c.x, y: c.y, n: k });
    }
    if (moved >= 0) {
      const st = r.strands[moved] as { at: number[] };
      const c = r.cells[st.at[w.front[moved] as number] as number] as Pt;
      spawn(s, 'puffball', c.x, c.y, r.puffBp);
    }
  }
  // The rot in the maple eats its branches.
  for (let k = 0; k < w.inMaple.length; k++) {
    const t = w.inMaple[k] as number;
    if (t >= 0 && s.tick >= t) {
      w.inMaple[k] = s.tick + r.eat;
      branch('rot', r.maple.x, r.maple.y);
    }
  }
  // The broodmother's webs on the maple: struck down in time, or a branch.
  for (const id of Object.keys(w.webs)) {
    const c = findEnemy(s, Number(id));
    const until = w.webs[Number(id)] as number;
    if (!c || c.hp <= 0) {
      delete w.webs[Number(id)];
      continue;
    }
    if (s.tick >= until) {
      delete w.webs[Number(id)];
      c.hp = 0;
      branch('web', c.x, c.y);
    }
  }
  // The brood on the hero: its silk slows it.
  if (h && s.tick % 6 === 0) {
    for (const e of s.enemies) {
      if (e.hp > 0 && e.kind === 'spiderling' && withinRange(e.x, e.y, h.x, h.y, r.brood.r)) {
        w.silk = s.tick + 12;
        break;
      }
    }
  }
  // Her leap landing.
  if (w.bt >= 0 && s.tick >= w.bt) {
    w.bt = -1;
    const boss = w.boss ? findEnemy(s, w.boss) : null;
    if (boss && boss.hp > 0) {
      boss.x = w.bx;
      boss.y = w.by;
    }
    ev(s, { t: 'senda', kind: 'leap', npc: '', x: w.bx, y: w.by });
    for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, w.bx, w.by, r.boss.r)) damageHero(s, x, mulBp(x.stats.maxHp, r.boss.bp), boss, 'physical', true);
  }
  // Out: every rotten root stopped, and the rot come up to each cut (it goes out for want of root there).
  if (w.lost === 0) {
    let out = true;
    for (let k = 0; k < r.strands.length; k++) {
      if (!(r.strands[k] as { rot: boolean }).rot) continue;
      const b = w.block[k] as number;
      if (b < 0 || (w.inMaple[k] as number) >= 0 || (w.front[k] as number) + 1 < b) out = false;
    }
    if (out) {
      w.done = s.tick;
      ev(s, { t: 'senda', kind: 'rotOut', npc: '', x: r.maple.x, y: r.maple.y });
      cue('out');
    }
  }
};

/** The broodmother: about the clearing near the hero; a leap at its spot (3 s); a web on the maple; her brood. */
const broodmother = (s: BattleState, r: NonNullable<AutumnRules['under']>, w: St, e: Enemy, px: number, cue: (c: string) => void): boolean => {
  const h = heroOf(s);
  e.stun = 0;
  if (w.done >= 0) {
    e.anim = 'idle';
    return true;
  }
  if (w.bt >= 0) {
    e.anim = 'attack';
    return true;
  }
  // The web on the maple.
  if (s.waveT >= r.web.first && s.tick >= w.webNext) {
    w.webNext = s.tick + r.web.every;
    const a = rngInt(s.rng, 3) - 1;
    const c = spawn(s, 'cocoon', r.maple.x + a * 1300, r.maple.y + 1500, r.web.hpBp);
    w.webs[c.id] = s.tick + r.web.cut;
    ev(s, { t: 'senda', kind: 'web', npc: '', x: c.x, y: c.y, n: r.web.cut });
    cue(`web${Object.keys(w.webs).length}`);
  }
  // Her brood.
  if (s.waveT >= r.brood.first && s.tick >= w.broodNext) {
    w.broodNext = s.tick + r.brood.every;
    for (let k = 0; k < r.brood.n; k++) spawn(s, 'spiderling', e.x + rngRange(s.rng, -600, 600), e.y + rngRange(s.rng, -600, 600));
    ev(s, { t: 'senda', kind: 'brood', npc: '', x: e.x, y: e.y });
  }
  // The leap: the hero's spot marked, she lands there.
  if (h && s.waveT >= r.boss.first && s.tick >= w.bossNext) {
    w.bossNext = s.tick + r.boss.every;
    w.bt = s.tick + r.boss.warn;
    w.bx = h.x;
    w.by = h.y;
    w.leaps++;
    ev(s, { t: 'senda', kind: 'leapWarn', npc: '', x: h.x, y: h.y, n: r.boss.warn });
    cue(`leap${w.leaps}`);
    return true;
  }
  // About: a little off the hero, toward the maple.
  if (h) {
    const tx = h.x + (h.x > r.maple.x ? -2200 : 2200);
    const ty = h.y - 1400;
    if (!withinRange(e.x, e.y, tx, ty, 500)) {
      e.anim = 'walk';
      stepToward(e, tx, ty, imax(px, r.boss.pace));
    } else e.anim = 'idle';
  }
  return true;
};

/* ---------- the act ---------- */

export const autumnTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.autumn;
  const sr = s.senda;
  if (!r || !sr) return;
  if (!sr.autumn) {
    const w = fresh();
    sr.autumn = w;
    if (r.under) {
      const p = r.under.rim[0] as Pt;
      const e = spawnEnemy(s, 'weaver', 1, false, 0, p.x, p.y, r.bossHp);
      w.boss = e.id;
      s.bossId = e.id;
      w.bossNext = r.under.boss.first;
      w.webNext = r.under.web.first;
      w.broodNext = r.under.brood.first;
    }
    if (r.blight) w.chop = 12;
  }
  const w = sr.autumn;
  if (!s.started) return;
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  if (r.leaves) leavesTick(s, r.leaves, w, cue);
  else if (r.blight) blightTick(s, r.blight, w, cue);
  else if (r.under) underTick(s, r.under, w, cue);
  w.t++;
};

/** A stalker under the leaves (or heaving them, or off under them): nothing reaches it (combat's `isRevealed`). */
export const autumnHidden = (s: BattleState, e: Enemy): boolean => {
  const m = s.senda?.autumn?.sk[e.id];
  return !!m && (m[0] === 0 || m[0] === 1 || m[0] === 3);
};

/**
 * What lands no blow on the path's people by standing at them (battle/senda.ts `struck`): a stalker under the leaves
 * or held by the roots, a weaver (its silk is its blow), a cocoon (its squeeze is counted here), a leech (it clings to
 * the hero).
 */
export const autumnSpares = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.autumn;
  if (!w) return false;
  if (e.kind === 'cocoon' || e.kind === 'leech' || w.wv[e.id]) return true;
  const m = w.sk[e.id];
  return !!m && m[0] !== 2;
};

/** Moved by the act's own rule (true): the stalkers, the weavers, the broodmother. */
export const autumnHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.autumn;
  const w = s.senda?.autumn;
  if (!r || !w) return false;
  const cue = (c: string): void => {
    w.pend.push(c);
  };
  if (r.leaves && w.sk[e.id]) return stalkerHold(s, r.leaves, w, e, cue);
  if (r.leaves && w.wv[e.id]) return weaverHold(s, r.leaves, w, e, px(e), cue);
  if (r.under && e.id === w.boss) return broodmother(s, r.under, w, e, px(e), cue);
  // A cocoon stays where it was spun.
  if (e.kind === 'cocoon') return true;
  return false;
};

/** The hero's pace in the wood: the brood's silk on it. */
export const autumnSpeedBp = (s: BattleState): number => {
  const u = s.rules?.senda?.autumn?.under;
  const w = s.senda?.autumn;
  return u && w && w.silk > s.tick ? 10000 - u.brood.slowBp : 10000;
};

/** Ceda walks her way herself (the path's base would stand her still). */
export const autumnWalks = (s: BattleState, n: SendaNpc): boolean => !!s.senda?.autumn?.walk && n.id === 'ceda';

/** Another order of the hero's own: the root or the cell it was sent to is off. */
export const autumnMarkOff = (s: BattleState): void => {
  const w = s.senda?.autumn;
  if (w) w.job = -1;
};

/** How far the goal has come: Ceda's way, the waves stopped, the rotten roots stopped. */
export const autumnGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.autumn;
  const w = s.senda?.autumn;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.leaves) {
    if (w.done >= 0) return { done: 1, need: 1 };
    const ceda = npcOf(s, 'ceda');
    const a = r.leaves.way[0] as Pt;
    const b = r.leaves.way[r.leaves.way.length - 1] as Pt;
    const total = imax(1, a.y - b.y);
    return { done: ceda ? imax(0, imin(total, a.y - ceda.y)) : 0, need: total };
  }
  if (r.blight) {
    // The waves come and gone (the last one still coming down counts once it is stopped).
    const n = r.blight.waves.length;
    return { done: w.done >= 0 ? n : imax(0, w.wave - (w.heads.length ? 1 : 0)), need: n };
  }
  const u = r.under;
  if (!u) return { done: 0, need: 1 };
  let need = 0;
  let done = 0;
  for (let k = 0; k < u.strands.length; k++) {
    if (!(u.strands[k] as { rot: boolean }).rot) continue;
    need++;
    if ((w.block[k] ?? -1) >= 0 && (w.inMaple[k] ?? -1) < 0) done++;
  }
  return { done, need: imax(1, need) };
};

/** Won a moment after the goal; lost with the rot at the houses or the maple's branches gone. */
export const autumnResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.autumn;
  const w = s.senda?.autumn;
  if (!r || !w) return null;
  if (w.lost) return 'lost';
  if (w.done >= 0) return s.tick >= w.done + (r.under ? 36 : 18) ? 'won' : null;
  return null;
};
